import React, { useEffect, useState } from "react";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { adminWaitlist, getStoreSettings, updateStoreSettings, notifyWaitlist } from "../../services/api";
import { toast } from "sonner";
import { BellRing, PackageX, Send, Loader2 } from "lucide-react";

export const AdminWaitlist = () => {
  const [entries, setEntries] = useState([]);
  const [soldOut, setSoldOut] = useState(false);
  const [sending, setSending] = useState(false);
  const pending = entries.filter((e) => !e.notified_at).length;

  useEffect(() => {
    adminWaitlist().then(setEntries).catch(() => {});
    getStoreSettings().then((s) => setSoldOut(!!s.sold_out)).catch(() => {});
  }, []);

  const toggle = async (val) => {
    setSoldOut(val);
    try {
      await updateStoreSettings(val);
      toast.success(val ? "Ausverkauft-Modus aktiviert — Banner sichtbar, Checkout blockiert" : "Shop wieder aktiv! Tipp: Jetzt die Warteliste mit ‚Alle benachrichtigen' informieren.");
    } catch {
      setSoldOut(!val);
      toast.error("Konnte Einstellung nicht speichern");
    }
  };

  const notifyAll = async () => {
    if (!window.confirm(`Wieder-lieferbar-Mail an ${pending} Empfänger senden?`)) return;
    setSending(true);
    try {
      const { queued } = await notifyWaitlist();
      toast.success(queued ? `Rundmail an ${queued} Empfänger wird versendet` : "Alle wurden bereits benachrichtigt");
      const stamp = new Date().toISOString();
      setEntries((prev) => prev.map((e) => (e.notified_at ? e : { ...e, notified_at: stamp })));
    } catch {
      toast.error("Rundmail fehlgeschlagen");
    }
    setSending(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-waitlist">
      <div className="bg-white rounded-2xl border border-border p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl text-ink flex items-center gap-2"><PackageX className="w-5 h-5 text-wine" /> Ausverkauft-Modus</h3>
          <p className="text-sm text-ink/55 mt-1">Aktiv: Banner auf allen Seiten, Checkout blockiert, Kunden können sich auf die Warteliste setzen.</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`text-sm font-medium ${soldOut ? "text-wine" : "text-ink/40"}`}>{soldOut ? "Ausverkauft" : "Lieferbar"}</span>
          <Switch data-testid="soldout-toggle" checked={soldOut} onCheckedChange={toggle} />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <div className="p-5 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h4 className="font-medium text-ink flex items-center gap-2"><BellRing className="w-4 h-4 text-gold" /> Warteliste ({entries.length})</h4>
          <Button data-testid="waitlist-notify-all-btn" onClick={notifyAll} disabled={sending || pending === 0} className="bg-wine hover:bg-wine-dark text-white rounded-full h-10 px-5 gap-2">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Alle benachrichtigen{pending > 0 ? ` (${pending} offen)` : ""}
          </Button>
        </div>
        {entries.length === 0 ? (
          <p className="p-6 text-sm text-ink/45">Noch keine Anmeldungen.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-cream-dark text-ink/60">
              <tr>
                <th className="text-left p-3 font-medium">Name</th>
                <th className="text-left p-3 font-medium">E-Mail</th>
                <th className="text-left p-3 font-medium">Angemeldet am</th>
                <th className="text-left p-3 font-medium">Benachrichtigt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {entries.map((e) => (
                <tr key={e.id} data-testid={`waitlist-row-${e.id}`}>
                  <td className="p-3 text-ink/80">{e.name || "—"}</td>
                  <td className="p-3"><a href={`mailto:${e.email}`} className="text-wine hover:underline">{e.email}</a></td>
                  <td className="p-3 text-ink/55">{new Date(e.created_at).toLocaleString("de-CH")}</td>
                  <td className="p-3 text-ink/55">{e.notified_at ? <span className="text-green-600">{new Date(e.notified_at).toLocaleString("de-CH")}</span> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
